import { AdvancedDynamicTexture, TextBlock, Image, Control, } from "@babylonjs/gui";
import { Vector2, Vector3, MeshBuilder, StandardMaterial, Color3, Mesh, Animation, Color4, ActionManager, ExecuteCodeAction, Quaternion, Tools, PointerEventTypes, ArcRotateCameraPointersInput, ArcRotateCameraMouseWheelInput, VertexBuffer, ParticleSystem, Texture, TransformNode, } from "@babylonjs/core";
export class createCamera {
    constructor(scene, options) {
        this.focusOption = undefined;
        let { activeCamera } = scene;
        let { x: defaultX = 0, y: defaultY = 0, z: defaultZ = 0, type = "rotate", alpha = 0, beta = 0, radius = activeCamera.radius, framePerSecond = 1, totalFrame = 1, translationVelocity = 1, } = options;
        this.options = options;
        this.scene = scene;
        this.camera = activeCamera;
        this.defaultX = defaultX;
        this.defaultY = defaultY;
        this.defaultZ = defaultZ;
        this.offsetX = 0;
        this.offsetY = 0;
        this.offsetZ = 0;
        this.focusA = 0;
        this.focusB = 0;
        this.type = type;
        this.translationVelocity = translationVelocity;
        if (activeCamera) {
            this.initSetting();
            activeCamera.setTarget(new Vector3(0, 0, 0));
            this.setType(type)
                .setTarget(0, 0, 0)
                .setRotate(alpha, beta)
                .animation("cameraRadius", "radius", radius, framePerSecond, totalFrame);
            this.initEvent();
        }
    }
    initSetting(options = this.options) {
        let activeCamera = this.camera;
        let excludedKeys = new Set([
            "x",
            "y",
            "z",
            "alpha",
            "beta",
            "radius",
            "target",
        ]);
        let includedKeys = new Map([{ name: "translationVelocity", value: 1 }].map((config) => [
            config.name,
            config.value,
        ]));
        Object.keys(options).forEach((key) => {
            var _a, _b;
            if (includedKeys.has(key)) {
                (_a = this.options)[key] ?? (_a[key] = this[key] ?? includedKeys.get(key));
                this[key] = options[key] ?? this.options[key];
            }
            else if (!excludedKeys.has(key) && key in activeCamera) {
                (_b = this.options)[key] ?? (_b[key] = activeCamera[key]);
                activeCamera[key] = options[key] ?? this.options[key];
            }
        });
    }
    initEvent() {
        let scene = this.scene;
        let dragStart = new Vector2(0, 0);
        let isDragging = false;
        let offsetX = 0, offsetY = 0;
        let camera = this.camera;
        scene.onPointerObservable.add((pointerInfo) => {
            if (this.isLock) {
                if (pointerInfo.type === PointerEventTypes.POINTERDOWN &&
                    pointerInfo.event.buttons === 4) {
                    isDragging = true;
                    camera.inputs.removeByType("ArcRotateCameraPointersInput");
                    camera.inputs.removeByType("ArcRotateCameraMouseWheelInput");
                }
                else if (pointerInfo.type === PointerEventTypes.POINTERUP &&
                    isDragging) {
                    isDragging = false;
                    camera.inputs.add(new ArcRotateCameraPointersInput());
                    camera.inputs.add(new ArcRotateCameraMouseWheelInput());
                    this.initSetting(this.focusOption);
                }
                else if (pointerInfo.type === PointerEventTypes.POINTERMOVE &&
                    isDragging) {
                    let { y } = camera.target;
                    y += pointerInfo.event.movementY * this.translationVelocity;
                    camera.target.y = y <= 0 ? 0 : y;
                }
                return;
            }
            const dragStrategies = {
                translate: {
                    shouldStart: (pi) => pi.type === PointerEventTypes.POINTERDOWN,
                    shouldEnd: (pi) => pi.type === PointerEventTypes.POINTERUP,
                    shouldMove: (pi) => pi.type === PointerEventTypes.POINTERMOVE && isDragging,
                    onStart: (pi) => { },
                    onEnd: (pi) => { },
                    onMove: (pi) => {
                        let { alpha } = camera;
                        offsetX += pi.event.movementX * this.translationVelocity;
                        offsetY += pi.event.movementY * this.translationVelocity;
                        let { x, y, z } = this.calc(offsetX, offsetY, alpha);
                        this.setTarget(x, y, z, false);
                    },
                },
                rotate: {
                    shouldStart: (pi) => pi.type === PointerEventTypes.POINTERDOWN && pi.event.buttons === 4,
                    shouldEnd: (pi) => pi.type === PointerEventTypes.POINTERUP && isDragging,
                    shouldMove: (pi) => pi.type === PointerEventTypes.POINTERMOVE && isDragging,
                    onStart: (pi) => {
                        camera.inputs.removeByType("ArcRotateCameraPointersInput");
                        camera.inputs.removeByType("ArcRotateCameraMouseWheelInput");
                    },
                    onEnd: (pi) => {
                        camera.inputs.add(new ArcRotateCameraPointersInput());
                        camera.inputs.add(new ArcRotateCameraMouseWheelInput());
                        this.initSetting();
                    },
                    onMove: (pi) => {
                        let { alpha } = camera;
                        offsetX += pi.event.movementX * this.translationVelocity;
                        offsetY += pi.event.movementY * this.translationVelocity;
                        let { x, y, z } = this.calc(offsetX, offsetY, alpha);
                        this.setTarget(x, y, z, false);
                    },
                },
            };
            const strategy = dragStrategies[this.type === "translate" ? "translate" : "rotate"];
            if (strategy.shouldStart(pointerInfo)) {
                isDragging = true;
                strategy.onStart(pointerInfo);
            }
            else if (strategy.shouldEnd(pointerInfo)) {
                isDragging = false;
                strategy.onEnd(pointerInfo);
            }
            else if (strategy.shouldMove(pointerInfo)) {
                strategy.onMove(pointerInfo);
            }
        });
        camera.onViewMatrixChangedObservable.add((e) => {
            this.onViewMatrixChangedObservable?.(e);
        });
        camera.onProjectionMatrixChangedObservable.add((e) => {
            this.onProjectionMatrixChangedObservable?.(e);
        });
    }
    calc(offsetX, offsetY, alpha) {
        const [sinA, cosA] = [Math.sin(alpha), Math.cos(alpha)];
        const [DEG45, DEG135] = [Math.PI / 4, (3 * Math.PI) / 4];
        const [dx, dz] = (() => {
            if (alpha < DEG45)
                return [-offsetY, -offsetX];
            if (alpha < DEG135)
                return [offsetX, -offsetY];
            return [offsetY, offsetX];
        })();
        const xComp = dx + dz * (alpha < DEG45 ? -sinA : alpha < DEG135 ? cosA : sinA);
        const zComp = dz + dx * (alpha < DEG45 ? sinA : alpha < DEG135 ? -cosA : -sinA);
        return { x: xComp, y: 0, z: zComp };
    }
    animation(name, property, value, framePerSecond = 30, totalFrame = 15) {
        Animation.CreateAndStartAnimation(name, this.camera, property, framePerSecond, totalFrame, this.camera[property], value, Animation.ANIMATIONLOOPMODE_CONSTANT);
        return this;
    }
    setType(type) {
        this.type = type;
        let camera = this.camera;
        if (type == "translate") {
            camera.lowerAlphaLimit = 0;
            camera.upperAlphaLimit = Math.PI;
            camera.inputs.removeByType("ArcRotateCameraPointersInput");
        }
        else {
            camera.lowerAlphaLimit = 0;
            camera.upperAlphaLimit = Math.PI * 2;
            camera.inputs.add(new ArcRotateCameraPointersInput());
        }
        return this;
    }
    setRadius(radius, animation = false) {
        let camera = this.camera;
        if (!camera)
            return this;
        animation
            ? this.animation("cameraRadius", "radius", radius)
            : (camera.radius = radius);
        return this;
    }
    setRotate(alpha, beta, animation = false) {
        let camera = this.camera;
        if (!camera)
            return this;
        animation
            ? this.animation("cameraAlpha", "alpha", (alpha * Math.PI) / 180).animation("cameraBeta", "beta", beta)
            : Object.assign(camera, { alpha: (alpha * Math.PI) / 180, beta });
        return this;
    }
    setTarget(x = 0, y = 0, z = 0, animation = false, scale = true) {
        let camera = this.camera;
        if (!camera)
            return this;
        this.offsetX = x;
        this.offsetY = y;
        this.offsetZ = z;
        let dx = this.defaultX, dy = this.defaultY, dz = this.defaultZ;
        let currentDirection = camera.position.subtract(camera.target).normalize();
        let currentDistance = Vector3.Distance(camera.position, camera.target);
        let newTarget = new Vector3(dx + x, dy + y, dz + z);
        let newPosition = newTarget.add(currentDirection.scale(currentDistance));
        const cameraStrategy = {
            animated: {
                setTarget: (target) => this.animation("cameraTarget", "target", target),
                setPosition: (position) => this.animation("cameraPosition", "position", position),
            },
            direct: {
                setTarget: (target) => camera.setTarget(target),
                setPosition: (position) => camera.setPosition(position),
            },
        };
        const strategy = animation
            ? cameraStrategy.animated
            : cameraStrategy.direct;
        strategy.setTarget(newTarget);
        scale && strategy.setPosition(newPosition);
        return this;
    }
    focus(building, option = {}) {
        let camera = this.camera;
        if (!camera)
            return this;
        this.isLock = true;
        this.focusOption = option;
        this.initSetting(option);
        if (this.type == "translate")
            camera.inputs.add(new ArcRotateCameraPointersInput());
        this.focusA = (this.camera.alpha / Math.PI) * 180;
        this.focusB = this.camera.beta;
        let { alpha = this.focusA, beta = this.focusB, radius = this.camera.radius, offsetX = 0, offsetY = 0, offsetZ = 0, } = option;
        let { x, z } = building.position;
        let newTarget = new Vector3(x + offsetX, 0 + offsetY, z + offsetZ);
        this.animation("cameraTarget", "target", newTarget)
            .animation("cameraAlpha", "alpha", (alpha * Math.PI) / 180)
            .animation("cameraBeta", "beta", beta)
            .animation("cameraRadius", "radius", radius)
            .onFocus?.();
        return this;
    }
    blur() {
        if (!this.isLock || !this.camera)
            return this;
        this.isLock = false;
        this.focusOption = undefined;
        let options = this.options;
        this.initSetting();
        let activeCamera = this.camera;
        let [a, b, r, x, y, z] = [
            options.type == "translate" ? options?.alpha : this.focusA,
            options.type == "translate" ? options?.beta : this.focusB,
            options?.radius || 0,
            options.type == "translate" ? this.offsetX : 0,
            options.type == "translate" ? this.offsetY : 0,
            options.type == "translate" ? this.offsetZ : 0,
        ];
        this.type == "translate" &&
            activeCamera.inputs.removeByType("ArcRotateCameraPointersInput");
        this.setTarget(x, y, z, true)
            .setRadius(r, true)
            .setRotate(a, b, true)
            .onBlur?.();
        return this;
    }
}
export function HexToRGB(hexColor, alpha = 1) {
    if (typeof hexColor == "undefined" || hexColor.length < 6) {
        return new Color4(1, 0, 1, alpha);
    }
    let hex = hexColor.charAt(0) == "#" ? hexColor.substring(1, 7) : hexColor;
    var r = parseInt(hex.substring(0, 2), 16) / 255;
    var g = parseInt(hex.substring(2, 4), 16) / 255;
    var b = parseInt(hex.substring(4, 6), 16) / 255;
    return new Color4(r, g, b, alpha);
}
export function RGBnum(color) {
    let numSrt = color.substring(4, color.length - 1).split(",");
    return new Color3(Number.parseInt(numSrt[0]) / 255 - 0.2, Number.parseInt(numSrt[1]) / 255 - 0.2, Number.parseInt(numSrt[2]) / 255 - 0.2);
}
export class createBuilding {
    constructor(meshes, scene, camera, highlightLayer, createBox, disposeSource = true) {
        this.isHighlight = false;
        this.scene = scene;
        this.camera = camera;
        this.highlightLayer = highlightLayer;
        this.isHover = false;
        this.noHighlight = false;
        this.isHighlight = false;
        let mesh = meshes.length > 1
            ? Mesh.MergeMeshes(meshes, disposeSource, true, undefined, true, true)
            : meshes[0];
        if (mesh) {
            this.mesh = mesh;
            let boundingInfo = mesh.getBoundingInfo();
            let boundingBox = boundingInfo.boundingBox;
            let centerLocal = Vector3.Lerp(boundingBox.minimum, boundingBox.maximum, 0.5);
            let centerWorld = Vector3.TransformCoordinates(centerLocal, mesh.getWorldMatrix());
            this.position = centerWorld;
            this.boundingBox = boundingBox;
            if (createBox) {
                let localMin = boundingBox.minimumWorld;
                let localMax = boundingBox.maximumWorld;
                let box = MeshBuilder.CreateBox("buildingBox", {
                    width: localMax.x - localMin.x,
                    height: localMax.y - localMin.y,
                    depth: localMax.z - localMin.z,
                }, scene);
                box.position = centerWorld;
                var material = new StandardMaterial("transparentMaterial", scene);
                material.alpha = 0;
                box.material = material;
                highlightLayer.addExcludedMesh(box);
                this.box = box;
            }
            this.initEvent();
        }
        else {
            this.position = new Vector3(0, 0, 0);
        }
    }
    addHighlight() {
        if (!this.mesh)
            return;
        this.highlightLayer.addMesh(this.mesh, Color3.White());
    }
    removeHighlight() {
        if (!this.mesh)
            return;
        this.highlightLayer.removeMesh(this.mesh);
    }
    OnPointerOverTrigger() {
        if (this.isHover || this.isHighlight || this.camera.isLock)
            return;
        this.isHover = true;
        if (!this.noHighlight)
            this.addHighlight();
        this.mouseEnter?.();
    }
    OnPointerOutTrigger() {
        if (!this.isHover || this.isHighlight || this.camera.isLock)
            return;
        this.isHover = false;
        if (!this.noHighlight)
            this.removeHighlight();
        this.mouseLeave?.();
    }
    OnPickTrigger() {
        this.onClick?.();
    }
    initEvent() {
        let scene = this.scene;
        let box = this.box || this.mesh;
        if (!box)
            return false;
        box.actionManager = new ActionManager(scene);
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, this.OnPointerOverTrigger.bind(this)));
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, this.OnPointerOutTrigger.bind(this)));
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger, this.OnPickTrigger.bind(this)));
    }
    toggleHighlight(isHighlight) {
        if (this.noHighlight)
            return this.removeHighlight();
        this.isHighlight =
            isHighlight !== undefined ? isHighlight : !this.isHighlight;
        this.isHighlight ? this.addHighlight() : this.removeHighlight();
    }
    useHighlight(noHighlight) {
        this.noHighlight =
            noHighlight !== undefined ? !noHighlight : !this.noHighlight;
    }
    setVisible(isVisible) {
        let mesh = this.mesh, box = this.box;
        if (mesh)
            mesh.isVisible = isVisible;
        if (box)
            box.isVisible = isVisible;
    }
    setEnabled(isEnabled) {
        let mesh = this.mesh, box = this.box;
        if (mesh)
            mesh.setEnabled(isEnabled);
        if (box)
            box.setEnabled(isEnabled);
    }
}
export class createPipeline {
    constructor(meshes, emitters, tubeOption, scene, camera, highlightLayer, auxiliary = false) {
        this.particleSystem = [];
        this.scene = scene;
        this.camera = camera;
        this.highlightLayer = highlightLayer;
        this.auxiliary = auxiliary;
        this.isAnimate = false;
        this.active = false;
        let mesh = meshes.length > 1
            ? Mesh.MergeMeshes(meshes, true, true, undefined, true, true)
            : meshes[0];
        this.meshes = meshes;
        this.mesh = mesh;
        this.material = mesh?.material;
        if (mesh) {
            this.renderingGroupId = mesh.renderingGroupId;
            let boundingInfo = mesh.getBoundingInfo();
            let boundingBox = boundingInfo.boundingBox;
            let centerLocal = Vector3.Lerp(boundingBox.minimum, boundingBox.maximum, 0.5);
            let centerWorld = Vector3.TransformCoordinates(centerLocal, mesh.getWorldMatrix());
            this.position = centerWorld;
            this.initEvent();
        }
        if (emitters.length) {
            this.initParticle(emitters);
        }
        if (tubeOption) {
            this.initTube(tubeOption);
        }
    }
    OnPointerOverTrigger() {
        if (this.isHover || this.camera.isLock)
            return;
        this.isHover = true;
        this.addHighlight();
        this.mouseEnter?.();
    }
    OnPointerOutTrigger() {
        if (!this.isHover || this.camera.isLock)
            return;
        this.isHover = false;
        this.removeHighlight();
        this.mouseLeave?.();
    }
    OnPickTrigger() {
        this.onClick?.();
    }
    initEvent() {
        let scene = this.scene;
        let mesh = this.mesh;
        if (!mesh)
            return false;
        mesh.actionManager = new ActionManager(scene);
        mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, this.OnPointerOverTrigger.bind(this)));
        mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, this.OnPointerOutTrigger.bind(this)));
        mesh.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger, this.OnPickTrigger.bind(this)));
    }
    initParticle(emitters) {
        if (this.isAnimate)
            return false;
        emitters.forEach((emitter) => {
            let { x = 0, y = 0, z = 0, length, lifeTime = 2, emitPower = 100, direction = { x: 0, y: 0, z: 0 }, } = emitter;
            if (length == undefined)
                length = emitPower;
            if (length == emitPower)
                return this.particleSystem.push(this.createEmitter(emitter));
            if (length < emitPower)
                return this.particleSystem.push(this.createEmitter({
                    ...emitter,
                    lifeTime: (length / emitPower) * lifeTime,
                }));
            if (length > emitPower) {
                const count = Math.floor(length / emitPower);
                const remainder = length % emitPower;
                const result = new Array(count).fill(emitPower);
                if (remainder > 0) {
                    result.push(remainder);
                }
                return result.map((len, i) => {
                    let newObj = { ...emitter };
                    if (len < emitPower)
                        newObj.lifeTime = (len / emitPower) * lifeTime;
                    if (direction.x !== undefined)
                        newObj.x += emitPower * i * direction.x;
                    if (direction.y !== undefined)
                        newObj.y += emitPower * i * direction.y;
                    if (direction.z !== undefined)
                        newObj.z += emitPower * i * direction.z;
                    return this.particleSystem.push(this.createEmitter(newObj));
                });
            }
        });
        this.isAnimate = true;
        this.setActive(false);
    }
    initTube(tubeOption) {
        let { tubes = [], ...option } = tubeOption;
        this.tubes = tubes
            .map((tube) => {
            return this.createTube(tube, option);
        })
            .flat();
    }
    createTube(tube, tubeOption) {
        let { path = [], ...option } = tube;
        let scene = this.scene;
        var originalPoints = [];
        let tubes = path.map((data) => {
            let { x, y, z, ...option } = data;
            let position = new Vector3(x, y, z);
            let box = MeshBuilder.CreateBox("tubes", { size: 10 }, scene);
            box.position = new Vector3(x, y, z);
            box.isVisible = this.auxiliary;
            originalPoints.push(position);
            return { option, position };
        });
        if (originalPoints.length < 2)
            return false;
        var varyingTube = MeshBuilder.CreateTube("varyingTube", { path: originalPoints, radius: 5, tessellation: 32 }, scene);
        varyingTube.isVisible = this.auxiliary;
        let pairs = tubes
            .slice(0, -1)
            .map((item, index) => [item, tubes[index + 1]]);
        pairs.forEach((pair) => {
            let vector = pair.map((d) => d.position);
            let opt = pair[0].option || {};
            this.createEmitterByDistance(vector, {
                ...tubeOption,
                ...option,
                ...opt,
            });
        });
    }
    getDirection(points = []) {
        let tangent, pointIndex = Math.floor(points.length / 2);
        if (pointIndex < points.length - 1)
            tangent = points[pointIndex + 1].subtract(points[pointIndex]).normalize();
        else
            tangent = points[pointIndex].subtract(points[pointIndex - 1]).normalize();
        return tangent;
    }
    createEmitterByDistance(vector, option) {
        let { emitPower = 100, lifeTime = 2 } = option;
        let pointStart = vector[0], pointEnd = vector[1], distance = Vector3.Distance(pointStart, pointEnd), direction = this.getDirection([pointStart, pointEnd]), count = Math.floor(distance / emitPower), remainder = distance % emitPower, result = new Array(count).fill(emitPower), emitter = {
            ...option,
            x: pointStart["x"],
            y: pointStart["y"],
            z: pointStart["z"],
            direction,
        };
        if (remainder > 0)
            result.push(remainder);
        result.forEach((len, i) => {
            let newObj = { ...emitter };
            if (len < emitPower)
                newObj.lifeTime = (len / emitPower) * lifeTime;
            if (direction.x !== undefined)
                newObj.x += emitPower * i * direction.x;
            if (direction.y !== undefined)
                newObj.y += emitPower * i * direction.y;
            if (direction.z !== undefined)
                newObj.z += emitPower * i * direction.z;
            this.particleSystem.push(this.createEmitter(newObj));
        });
    }
    createEmitter(option = {}) {
        let scene = this.scene;
        let { color = "#ffffff", texture = "assets/images/flare.png", x = 0, y = 0, z = 0, size = 32, emitRate = 1, lifeTime = 2, initialRotation = 0, direction = { x: 0, y: 0, z: 0 }, emitPower = 100, blendMode = "BLENDMODE_ONEONE", } = option;
        const envPrefix = process.env.REACT_APP_ENV !== "development" ? "/datav/" : "";
        const particleSystem = new ParticleSystem("particles", 10, scene);
        const emitter = MeshBuilder.CreateBox("emitter", { size: 10 }, scene);
        emitter.position = new Vector3(x, y, z);
        emitter.isVisible = this.auxiliary;
        particleSystem.particleTexture = new Texture(envPrefix + texture, scene);
        particleSystem.minSize = size;
        particleSystem.maxSize = size;
        particleSystem.minLifeTime = lifeTime / 2;
        particleSystem.maxLifeTime = lifeTime / 2;
        particleSystem.emitRate = emitRate;
        particleSystem.minEmitPower = emitPower;
        particleSystem.maxEmitPower = emitPower;
        particleSystem.gravity = new Vector3(0, 0, 0);
        particleSystem.minEmitBox = new Vector3(0, 0, 0);
        particleSystem.maxEmitBox = new Vector3(0, 0, 0);
        particleSystem.direction1 = new Vector3(direction.x || 0, direction.y || 0, direction.z || 0);
        particleSystem.direction2 = new Vector3(direction.x || 0, direction.y || 0, direction.z || 0);
        particleSystem.minInitialRotation = initialRotation;
        particleSystem.maxInitialRotation = initialRotation;
        particleSystem.color1 = new Color4(1, 1, 1, 1);
        particleSystem.color2 = new Color4(1, 1, 1, 1);
        particleSystem.colorDead = new Color4(1, 1, 1, 1);
        particleSystem.renderingGroupId = -1;
        particleSystem.isLocal = true;
        const resolvedBlendMode = ParticleSystem[blendMode];
        particleSystem.blendMode =
            typeof resolvedBlendMode !== "undefined"
                ? resolvedBlendMode
                : ParticleSystem.BLENDMODE_ONEONE;
        particleSystem.start();
        return particleSystem;
    }
    setActive(active = true) {
        this.active = active;
        let renderingGroupId;
        if (active) {
            this.addHighlight();
            renderingGroupId = this.mesh.renderingGroupId
                ? this.mesh.renderingGroupId + 1
                : 1;
        }
        else {
            this.removeHighlight();
            renderingGroupId = -1;
        }
        this.particleSystem.forEach((ps) => (ps.renderingGroupId = renderingGroupId));
    }
    toggleActive() {
        this.setActive(!this.active);
    }
    addHighlight() {
        if (!this.mesh)
            return;
        this.mesh.renderingGroupId = 1;
        const material = new StandardMaterial("pipeStandardMaterial", this.scene);
        material.diffuseColor = Color3.FromHexString("#99e1ff");
        this.mesh.material = material;
    }
    removeHighlight() {
        if (!this.mesh || this.active)
            return;
        this.mesh.material = this.material;
        this.mesh.renderingGroupId = this.renderingGroupId;
    }
}
export class centralToast {
    constructor() {
        let gui_texture = AdvancedDynamicTexture.CreateFullscreenUI("UI");
        let textBlock = new TextBlock();
        textBlock.text = "加载中···";
        textBlock.color = "white";
        textBlock.fontSize = 20;
        textBlock.textWrapping = true;
        textBlock.width = 0.5;
        textBlock.height = 0.2;
        textBlock.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        textBlock.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        gui_texture.addControl(textBlock);
        this.textBlock = textBlock;
    }
    setContent(text) {
        this.textBlock.text = text;
    }
    clearContent() {
        this.textBlock.text = "";
    }
}
export class createPoint {
    constructor(name, scene, options, data) {
        this.animationId = null;
        let { x, y, z, depth = 10, pixelRatio = 2, scale = 1, billboardmode = "BILLBOARDMODE_ALL", rotationY = Math.PI, fontSize = 20, renderingGroupId, color = "#f76260", source = "assets/images/point_error_gif.png", activeColor = "#fff", activeSource = "assets/images/point_gif2.png", } = options;
        this.name = name;
        this.position = { x, y, z };
        this.fontSize = fontSize * scale;
        this.color = color;
        this.source = source;
        this.scene = scene;
        this.activeColor = activeColor;
        this.activeSource = activeSource;
        let width = 56 * scale, height = 56 * scale, material = new StandardMaterial("transparentMaterial", scene);
        material.alpha = 0;
        this.box = MeshBuilder.CreateBox("textBox", {
            width: width / pixelRatio,
            height: height / pixelRatio,
            depth: depth / pixelRatio,
        }, scene);
        this.box.material = material;
        this.box.position = new Vector3(x, y, z);
        this.box.rotation.y = rotationY;
        this.textPlane = MeshBuilder.CreatePlane("textPlane", { width, height }, scene);
        if (renderingGroupId) {
            this.textPlane.renderingGroupId = renderingGroupId;
            this.box.renderingGroupId = renderingGroupId;
        }
        this.textPlane.isPickable = false;
        this.textPlane.position = new Vector3(x, y, z);
        this.textPlane.rotation.y = rotationY;
        let modeMap = {
            BILLBOARDMODE_NONE: Mesh.BILLBOARDMODE_NONE,
            BILLBOARDMODE_ALL: Mesh.BILLBOARDMODE_ALL,
            BILLBOARDMODE_Y: Mesh.BILLBOARDMODE_Y,
            BILLBOARDMODE_X: Mesh.BILLBOARDMODE_X,
        };
        if (billboardmode != "BILLBOARDMODE_NONE") {
            this.textPlane.rotationQuaternion = Quaternion.RotationYawPitchRoll(Tools.ToRadians(360), 0, 0);
            this.textPlane.billboardMode = modeMap[billboardmode];
        }
        this.planeGUI = AdvancedDynamicTexture.CreateForMesh(this.textPlane, width * pixelRatio, height * pixelRatio);
        this.data = data;
        const btnImage = new Image("textBg", this.source);
        btnImage.width = `${width}px`;
        btnImage.height = `${height}px`;
        btnImage.top = "0";
        btnImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        btnImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        this.planeGUI.addControl(btnImage);
        btnImage.cellWidth = 56;
        btnImage.cellHeight = 56;
        btnImage.cellId = 1;
        let frame = 1;
        let lastTime = 0;
        const frameInterval = 100;
        const animate = (timestamp) => {
            if (timestamp - lastTime >= frameInterval) {
                frame = (frame % 10) + 1;
                btnImage.cellId = frame;
                lastTime = timestamp;
            }
            this.animationId = requestAnimationFrame(animate);
        };
        this.animationId = requestAnimationFrame(animate);
        btnImage.zIndex = 10;
        this.button = btnImage;
        this.textBlock = new TextBlock();
        this.createTextBlock();
        this.initEvent();
    }
    initEvent() {
        let scene = this.scene;
        let box = this.box;
        box.actionManager = new ActionManager(scene);
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger, (pointInfo) => {
            this.onClick?.(pointInfo, this.data);
        }));
    }
    setActive(active) {
        if (active) {
            this.textBlock.color = this.activeColor;
            this.button.source = this.activeSource;
        }
        else {
            this.textBlock.color = this.color;
            this.button.source = this.source;
        }
    }
    setVisible(isVisible) {
        this.box.isVisible = isVisible;
        this.textPlane.isVisible = isVisible;
    }
    setEnabled(isEnabled) {
        this.box.setEnabled(isEnabled);
        this.textPlane.setEnabled(isEnabled);
    }
    dispose() {
        cancelAnimationFrame(this.animationId);
        this.planeGUI.dispose();
        this.button.dispose();
        this.box.dispose();
        this.textPlane.dispose();
        this.textBlock.dispose();
    }
    createTextBlock() {
        let fontSize = this.fontSize;
        let color = this.color;
        this.textBlock.dispose();
        this.planeGUI.removeControl(this.textBlock);
        let { name = "" } = this.data;
        const textBlock = new TextBlock();
        let len = name.split("\n").length >= 2 ? 2 : 1;
        textBlock.text = `${name}`;
        textBlock.color = color;
        textBlock.fontSize = fontSize;
        textBlock.fontWeight = "bold";
        textBlock.isVisible = true;
        textBlock.isHitTestVisible = false;
        textBlock.top = `${-fontSize / 2}px`;
        textBlock.left = `${0}px`;
        textBlock.lineSpacing = `${-23 - fontSize / 2}px`;
        textBlock.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        textBlock.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        textBlock.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        this.planeGUI.addControl(textBlock);
        this.textBlock = textBlock;
    }
    updateData(data) {
        this.data = data;
        this.createTextBlock();
    }
}
export function generateBasicUVs(mesh) {
    if (mesh.getVerticesData(VertexBuffer.UVKind))
        return mesh;
    var positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions)
        return mesh;
    var uvs = [];
    var min = new Vector3(Infinity, Infinity, Infinity);
    var max = new Vector3(-Infinity, -Infinity, -Infinity);
    for (var i = 0; i < positions.length; i += 3) {
        min.x = Math.min(min.x, positions[i]);
        min.y = Math.min(min.y, positions[i + 1]);
        min.z = Math.min(min.z, positions[i + 2]);
        max.x = Math.max(max.x, positions[i]);
        max.y = Math.max(max.y, positions[i + 1]);
        max.z = Math.max(max.z, positions[i + 2]);
    }
    var size = max.subtract(min);
    for (var i = 0; i < positions.length; i += 3) {
        var x = positions[i];
        var y = positions[i + 1];
        var z = positions[i + 2];
        uvs.push((x - min.x) / size.x, (z - min.z) / size.z);
    }
    mesh.setVerticesData(VertexBuffer.UVKind, uvs);
    return mesh;
}
export function hasParent(mesh, pattern) {
    if (mesh && mesh.parent) {
        if (RegExp(pattern).test(mesh.parent.name))
            return true;
        return hasParent(mesh.parent, pattern);
    }
    return false;
}
export function hasMesh(mesh, pattern) {
    if (!mesh)
        return false;
    return RegExp(pattern).test(mesh.name);
}
export function filterUVMesh(mesh, data) {
    let { pattern = "", parentNode, meshes, filterUV = false } = data;
    let { name } = mesh;
    if (!name)
        return false;
    let UVKind = mesh.getVerticesData(VertexBuffer.UVKind);
    let hasUv = filterUV ? UVKind : true;
    let hasMeshMatch = false;
    if (parentNode) {
        hasMeshMatch = hasParent(mesh, parentNode) && hasMesh(mesh, pattern);
    }
    else if (pattern)
        hasMeshMatch = hasMesh(mesh, pattern);
    if (!hasMeshMatch && Array.isArray(meshes) && meshes.length) {
        hasMeshMatch = meshes.some((m) => {
            if (m.parentNode)
                return hasParent(mesh, m.parentNode) && hasMesh(mesh, m.pattern);
            else
                return hasMesh(mesh, m.pattern);
        });
    }
    if (hasUv && hasMeshMatch && !filterUV && !UVKind) {
        console.warn("导入的网格缺少UV:", name);
        generateBasicUVs(mesh);
    }
    return hasUv && hasMeshMatch;
}
export function filterMesh(option, scene) {
    let meshes = scene.meshes;
    return meshes.filter((mesh) => {
        if (typeof option != "object")
            return option && mesh instanceof Mesh && mesh.name.includes(option);
        let { name, pattern, parentNode } = option;
        if (parentNode && !hasParent(mesh, parentNode))
            return false;
        if (!pattern)
            return mesh instanceof Mesh && (name ? mesh.name.includes(name) : true);
        return (mesh instanceof Mesh &&
            hasMesh(mesh, pattern) &&
            (name ? mesh.name.includes(name) : true));
    });
}
export function getNodesByName(scene, nodeName) {
    let allNodes = scene.getNodes();
    return allNodes.filter((node) => node.name === nodeName);
}
export function filterNode(option, scene) {
    let { name, pattern, parentNode, parentNodes } = option;
    if (parentNode || parentNodes) {
        let parent = parentNodes
            ? getNodesByName(scene, parentNodes)
            : [scene.getNodeByName(parentNode)].filter((d) => d);
        if (!parent.length)
            return [];
        let children = parent.length == 1
            ? parent[0].getChildren()
            : parent.map((p) => p.getChildren()).flat();
        if (pattern)
            return children.filter((child) => hasMesh(child, pattern) && (name ? child.name.includes(name) : true));
        else
            return children.filter((child) => name ? child.name.includes(name) : true);
    }
    else if (pattern) {
        let nodes = scene.getNodes();
        return nodes.filter((node) => hasMesh(node, pattern) && (name ? node.name.includes(name) : true));
    }
    return [scene.getNodeByName(name)];
}
export function toggleNodeMesh(node, boolean) {
    if (node instanceof Mesh)
        return (node.isVisible = !!boolean);
    if (node instanceof TransformNode)
        return node.setEnabled(!!boolean);
}
export class createMesh {
    constructor(meshes, scene, camera, highlightLayer, disposeSource = true) {
        this.isHighlight = false;
        this.scene = scene;
        this.camera = camera;
        this.highlightLayer = highlightLayer;
        this.isHover = false;
        this.noHighlight = false;
        this.isHighlight = false;
        let mesh = meshes.length > 1
            ? Mesh.MergeMeshes(meshes, disposeSource, true, undefined, true, true)
            : meshes[0];
        if (mesh) {
            this.mesh = mesh;
            this.material = mesh.material;
            this.initEvent();
        }
    }
    addHighlight() {
        if (!this.mesh)
            return;
        this.highlightLayer.addMesh(this.mesh, Color3.FromHexString("#99e1ff"));
        const material = new StandardMaterial("meshStandardMaterial", this.scene);
        material.diffuseColor = Color3.FromHexString("#00c1ff");
        material.emissiveColor = new Color3(0.3, 0.3, 0.3);
        this.mesh.material = material;
    }
    removeHighlight() {
        if (!this.mesh)
            return;
        this.mesh.material = this.material;
        this.highlightLayer.removeMesh(this.mesh);
    }
    OnPointerOverTrigger() {
        if (this.isHover || this.isHighlight)
            return;
        this.isHover = true;
        if (!this.noHighlight)
            this.addHighlight();
        this.mouseEnter?.();
    }
    OnPointerOutTrigger() {
        if (!this.isHover || this.isHighlight)
            return;
        this.isHover = false;
        if (!this.noHighlight)
            this.removeHighlight();
        this.mouseLeave?.();
    }
    OnPickTrigger(pointInfo) {
        this.onClick?.(pointInfo);
    }
    initEvent() {
        let scene = this.scene;
        let box = this.box || this.mesh;
        if (!box)
            return false;
        box.actionManager = new ActionManager(scene);
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOverTrigger, this.OnPointerOverTrigger.bind(this)));
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPointerOutTrigger, this.OnPointerOutTrigger.bind(this)));
        box.actionManager.registerAction(new ExecuteCodeAction(ActionManager.OnPickTrigger, this.OnPickTrigger.bind(this)));
    }
    toggleHighlight(isHighlight) {
        if (this.noHighlight)
            return this.removeHighlight();
        this.isHighlight =
            isHighlight !== undefined ? isHighlight : !this.isHighlight;
        this.isHighlight ? this.addHighlight() : this.removeHighlight();
    }
    useHighlight(noHighlight) {
        this.noHighlight =
            noHighlight !== undefined ? !noHighlight : !this.noHighlight;
    }
}
export class createTexture {
    constructor(scene, options, parent) {
        let { x, y, z, width = 100, height = 100, billboardmode = "BILLBOARDMODE_NONE", name = "texturePlane", source = "", rotationY = Math.PI, rotationX = 0, } = options;
        this.source = source;
        this.scene = scene;
        this.textPlane = MeshBuilder.CreatePlane(name, { width, height }, scene);
        this.textPlane.isPickable = false;
        this.textPlane.position = new Vector3(x, y, z);
        this.textPlane.rotation.y = rotationY;
        this.textPlane.rotation.x = rotationX;
        let modeMap = {
            BILLBOARDMODE_NONE: Mesh.BILLBOARDMODE_NONE,
            BILLBOARDMODE_ALL: Mesh.BILLBOARDMODE_ALL,
            BILLBOARDMODE_Y: Mesh.BILLBOARDMODE_Y,
            BILLBOARDMODE_X: Mesh.BILLBOARDMODE_X,
        };
        if (billboardmode != "BILLBOARDMODE_NONE") {
            this.textPlane.rotationQuaternion = Quaternion.RotationYawPitchRoll(Tools.ToRadians(360), 0, 0);
            this.textPlane.billboardMode = modeMap[billboardmode];
        }
        this.planeGUI = AdvancedDynamicTexture.CreateForMesh(this.textPlane, width, height);
        const texture = new Image("texture", this.source);
        texture.width = `${width}px`;
        texture.height = `${height}px`;
        texture.top = "0";
        texture.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        texture.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        this.planeGUI.addControl(texture);
        this.texture = texture;
        if (parent)
            this.textPlane.parent = parent;
    }
    dispose() {
        this.planeGUI.dispose();
        this.texture.dispose();
        this.textPlane.dispose();
    }
}
export async function preloadImages(urls, onProgress) {
    const results = [];
    let loaded = 0;
    const loadSingleImage = function (url) {
        return new Promise((resolve, reject) => {
            const img = new window.Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`加载失败: ${url}`));
            img.src = url;
        });
    };
    for (let i = 0; i < urls.length; i++) {
        try {
            const img = await loadSingleImage(urls[i]);
            results.push({
                url: urls[i],
                status: "success",
                img: img,
            });
        }
        catch (error) {
            results.push({
                url: urls[i],
                status: "error",
                error: error?.message ?? String(error),
            });
        }
        loaded++;
        if (onProgress) {
            onProgress(loaded, urls.length, results);
        }
    }
    return results;
}
